/**
 * Admin users API client — Story 10.2 (Epic 10 — Admin User Management) e
 * Story 15.1 (Epic 15 — convite de usuário).
 *
 * Thin fetch helpers for `apps/api/src/routes/users.ts` (all admin-only; the
 * server `requireAdmin` is the real gate). Cookies (better-auth session) are
 * always sent. Non-2xx surfaces as a thrown `ApiError` carrying the HTTP status
 * + a server-provided problem title, so the UI can distinguish 409 (last-admin)
 * from 422 (self-action) and show the precise message (AC-15).
 *
 * O lado PÚBLICO do convite (a tela `/accept-invite`, que corre sem sessão)
 * vive em `lib/api/invitations.ts` — perímetros diferentes, ficheiros
 * diferentes. As duas metades partilham a classe `ApiError` daqui, porque o
 * `instanceof` das telas tem de bater com o que a rede lança.
 */
import type {
  GenesisPortalAccess,
  ManagedCustomerConflict,
  ManagedCustomers,
  ManagedCustomersUpdate,
} from '@caliber/shared'
import { API_BASE } from '@/lib/api'

/**
 * O papel tal como a LEITURA o devolve — `GET /api/users` e
 * `GET /api/users/invitations`.
 *
 * `'customer'` entrou com o Portal do Cliente F1. Não é um papel que esta tela
 * conceda: é um papel que ela tem de saber LER, porque o servidor já o devolve
 * (`AdminUserItem.role` em `apps/api/src/routes/users.ts`) e uma conta de
 * cliente tem acesso — ao portal dela. Enquanto esta união dizia
 * `'admin' | 'recruiter'`, o `listUsers` continuava a devolver linhas de
 * cliente e o `as AdminUser[]` não as apanhava: o desvio não era um erro de
 * compilação, era uma linha desenhada com o papel errado.
 *
 * A união irmã da SESSÃO vive em `lib/api/me.ts` e já incluía `'customer'`.
 * São duas de propósito — aquela é o que a guarda de rota compara, esta é o
 * contrato de administração de usuários — mas as duas leem a MESMA coluna, e
 * portanto têm de aceitar os mesmos valores.
 */
export type UserRole = 'admin' | 'recruiter' | 'customer'

/**
 * O subconjunto que esta tela pode ATRIBUIR. Espelha, literalmente, o
 * `z.enum(['admin', 'recruiter'])` do `UpdateRoleSchema` do servidor.
 *
 * A assimetria leitura/escrita é o ponto, e é do servidor: `role='customer'`
 * sem `customer_id` é um estado inválido (a conta fica trancada fora do ATS e
 * fora do portal), então a pertença a um cliente decide-se no CONVITE, no
 * único instante em que papel e dono são escritos juntos. Tipar a escrita com
 * a união de LEITURA deixaria o `updateUserRole` e o diálogo de convite
 * compilarem pedidos que o servidor recusa com 422/400.
 */
export type AssignableUserRole = 'admin' | 'recruiter'

export type UserStatus = 'active' | 'disabled'

/** One row of the admin users list (mirrors `AdminUserItem` in routes/users.ts). */
export interface AdminUser {
  id: string
  name: string
  email: string
  /** LEITURA — pode ser `'customer'`. Ver `UserRole`. */
  role: UserRole
  status: UserStatus
  createdAt: string
  /** ISO of the most recent successful login, or null ("Nunca") if never. */
  lastAccessAt: string | null
  /**
   * O cliente a que a conta pertence — `null` em toda a gente da Genesis.
   *
   * ⚠️ O PAR (`role`, `customerId`) É QUE DESCREVE A CONTA, e não o `role`
   * sozinho: `'customer'` sem cliente é uma conta TRANCADA (sem ATS pela
   * barreira do `requireAuth`, sem portal por não ter dono). Sem os dois campos
   * lado a lado, a grade desenha uma conta partida exactamente igual a uma sã —
   * e é nesta tela que alguém decide desativá-la.
   */
  customerId: string | null
  /** `null` também quando o cliente já não existe: o join do servidor é LEFT. */
  customerName: string | null
  /**
   * O perfil de cliente da pessoa da Genesis (issue #584). `null` sem perfil (e em toda
   * conta de cliente). As áreas do perfil vêm do `getGenesisPortal`. Opcional porque as
   * suítes encenam a linha.
   */
  genesisPortal?: {
    clientCustomerId: string | null
    clientCustomerName: string | null
  } | null
  /**
   * Os clientes de que a pessoa é gestora de conta (Story 35.9), por nome. Opcional
   * pela razão do `genesisPortal`; ausente é nenhum.
   */
  managedCustomers?: Array<{ id: string; name: string }>
  /**
   * A conta é owner (Story 35.10): a marca, só num admin da Genesis ativo. Opcional pela
   * razão do `genesisPortal`; ausente é «não é owner».
   */
  isOwner?: boolean
  /**
   * O gestor do cliente ATIVO ligado à conta (Story 38.7), no cliente dela ou no do perfil
   * de cliente: é dele que vem o que a conta vê no portal. `null` = sem gestor, e a conta
   * `customer` não entra no portal. Com dois (a migração pode deixá-los), o mais antigo.
   * Opcional pela razão do `genesisPortal`; ausente é «sem gestor».
   */
  customerManager?: { id: string; name: string } | null
}

/** Error with the HTTP status + the server problem title (for toast copy). */
export class ApiError extends Error {
  readonly status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

const USERS_URL = `${API_BASE}/users`

/** Read the RFC-7807 `title` (or a generic fallback) from a non-2xx response. */
async function problemTitle(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { title?: string }
    return body.title ?? fallback
  } catch {
    return fallback
  }
}

/** GET /api/users — the full user list (admin only). */
export async function listUsers(signal?: AbortSignal): Promise<AdminUser[]> {
  const res = await fetch(USERS_URL, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível carregar os usuários'))
  }
  return (await res.json()) as AdminUser[]
}

/**
 * Uma pessoa da aba Genesis (Story 35.8, #655): do auth, com a conta do Caliber quando a
 * tem. Espelha `GenesisPersonItem` em `routes/users.ts`.
 */
export interface GenesisPerson {
  /** O `sub` do auth. `null` numa conta do Caliber que já não está na lista do auth. */
  sub: string | null
  name: string
  email: string
  /** O nível no auth, como papel do Caliber. `null` = sem acesso no auth. */
  level: UserRole | null
  /** A conta do Caliber. `null` = ainda não entrou no Caliber. */
  account: AdminUser | null
}

/**
 * `GET /api/users/genesis`. `unavailable` não é uma falha do pedido: é a aba sem lista,
 * com o motivo — `no_genesis_session` quando quem pede entrou com senha, porque a
 * consulta do auth pede o token da conta Genesis.
 */
export type GenesisUsers =
  | {
      source: 'auth'
      people: GenesisPerson[]
      /** Os pré-acessos abertos no auth, que ainda ninguém usou. */
      pending: Array<{ email: string; level: UserRole }>
    }
  | { source: 'unavailable'; reason: 'no_genesis_session' | 'token' | 'access' | 'error' }

/** GET /api/users/genesis — quem é da Genesis, lido do auth (admin, login da Genesis ligado). */
export async function listGenesisUsers(signal?: AbortSignal): Promise<GenesisUsers> {
  const res = await fetch(`${USERS_URL}/genesis`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível carregar a Genesis'))
  }
  return (await res.json()) as GenesisUsers
}

/**
 * PATCH /api/users/:id/role — set the target's role.
 *
 * `AssignableUserRole` e não `UserRole`: o servidor recusa `'customer'` neste
 * endpoint (422) e recusa também MUDAR o papel de quem já é cliente. Ver a
 * nota do tipo.
 */
export async function updateUserRole(id: string, role: AssignableUserRole): Promise<void> {
  const res = await fetch(`${USERS_URL}/${id}/role`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ role }),
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível alterar o papel'))
  }
}

/**
 * PUT /api/users/:id/owner — torna (`true`) ou tira (`false`) um owner (Story 35.10).
 * Só um owner o faz (403); só um admin da Genesis ativo pode ser owner (422); o último
 * owner não sai (409). O erro leva o título do servidor, para o toast.
 */
export async function setUserOwner(id: string, owner: boolean): Promise<void> {
  const res = await fetch(`${USERS_URL}/${id}/owner`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ owner }),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await problemTitle(
        res,
        owner ? 'Não foi possível tornar o usuário owner' : 'Não foi possível tirar o owner',
      ),
    )
  }
}

/** PATCH /api/users/:id/disable — soft-disable the account. */
export async function disableUser(id: string): Promise<void> {
  const res = await fetch(`${USERS_URL}/${id}/disable`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível desativar o usuário'))
  }
}

/** PATCH /api/users/:id/enable — reactivate the account. */
export async function enableUser(id: string): Promise<void> {
  const res = await fetch(`${USERS_URL}/${id}/enable`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível reativar o usuário'))
  }
}

// NÃO HÁ `deleteUser`, E É DE PROPÓSITO.
//
// `DELETE /api/users/:id` responde 405 com `Allow: GET, PATCH`. Uma conta
// desativa-se (`disableUser`) e reativa-se (`enableUser`); não se remove, porque
// o histórico que ela criou — avaliações, vagas, candidaturas — continua a
// precisar do autor, e `evaluations.created_by` é NOT NULL. A implementação
// antiga apagava `sessions → accounts → users` e ignorava nove colunas
// `created_by`, portanto dava 500 para qualquer usuário com história.
//
// O 405 fica de pé para os bundles antigos em cache: quem ainda tiver a versão
// anterior desta tela vai carregar em "Excluir" e recebe uma frase legível.

// ---------------------------------------------------------------------------
// Convites (Story 15.1) — o admin não define a senha de ninguém: convida.
// ---------------------------------------------------------------------------

const INVITATIONS_URL = `${USERS_URL}/invitations`

/** Um convite ainda por aceitar (mirrors `GET /api/users/invitations`). */
export interface UserInvitation {
  id: string
  name: string
  email: string
  /**
   * LEITURA, e portanto a união larga: o `POST /invite` do servidor ACEITA
   * `'customer'` (com `customerId` obrigatório), logo a lista de pendentes
   * pode devolver um convite de cliente. É por isso que a grade de convites
   * usa o mesmo `RolePill` da grade de usuários — se ele não soubesse ler
   * `'customer'`, o convite de um cliente apareceria rotulado "Recruiter", que
   * é a mesma mentira, na segunda tabela da mesma tela.
   */
  role: UserRole
  /** ISO — a partir daqui o token deixa de ser aceite (o servidor devolve 404). */
  expiresAt: string
  createdAt: string
}

/**
 * Corpo do `POST /api/users/invite` — uma UNIÃO DISCRIMINADA, e é o ponto.
 *
 * Este é o único endpoint do produto onde `role='customer'` é aceite, porque é o
 * único instante em que o papel e o dono se escrevem no MESMO ato. O servidor
 * impõe a paridade com dois `.refine` (`routes/users.ts`), e as duas metades que
 * ele recusa são exactamente estas:
 *
 *   • `customer` SEM cliente → conta trancada fora de tudo (sem ATS, sem portal);
 *   • Genesis COM cliente → um `customer_id` pendurado num admin, que hoje
 *     ninguém lê e amanhã lê.
 *
 * ⚠️ A UNIÃO EXISTE PARA TORNAR OS DOIS ERROS INEXPRIMÍVEIS EM TYPESCRIPT, em vez
 * de descobertos num 400. Com um `role` largo e um `customerId?` opcional, os
 * dois casos compilam e o formulário só falha em produção — que é como a
 * escalada de privilégio da F1 chegou lá. `role` é o discriminante; `customerId`
 * só existe no ramo em que é obrigatório.
 *
 * O CONVITE DE CLIENTE NASCE DO GESTOR (Story 38.7, a #675): leva o
 * `customerManagerId`, e a aceitação liga a conta nova a esse gestor, cujas operações
 * são o que ela vê. A única porta é o painel do gestor (`use-convite-do-gestor.ts`); o
 * acesso por áreas de antes (`portalAllAreas`, `portalAreaIds`) saiu, e o servidor
 * responde 400 ao corpo antigo.
 */
export type InviteUserInput =
  | {
      name: string
      email: string
      role: AssignableUserRole
      // ⚠️ `?: never` e não a simples ausência (TEST-001 do QA da 24.5): o TypeScript
      // não verifica propriedades a mais que chegam por SPREAD num literal. Declaradas
      // como `never`, as chaves do cliente deixam de caber neste ramo, venham escritas à
      // mão ou espalhadas.
      customerId?: never
      customerManagerId?: never
    }
  | {
      name: string
      email: string
      role: 'customer'
      customerId: string
      /** O gestor do cliente de quem o convite nasce: o e-mail tem de ser o dele. */
      customerManagerId: string
    }

/**
 * Resposta do `POST /api/users/invite`.
 *
 * `emailSent === false` NÃO é uma falha: o convite existe, está gravado e o
 * `inviteUrl` é válido — só o envio pelo Resend é que não saiu. Por isso o
 * campo é um booleano na resposta 201 e não um erro: quem convida continua a
 * poder entregar o link à mão.
 */
export interface InviteResult {
  invitation: UserInvitation & { status: 'pending' }
  inviteUrl: string
  emailSent: boolean
}

/**
 * A recusa do convite, com o que o painel do gestor precisa para a pôr no sítio certo
 * (Story 38.7): a frase (`detail`, ou o `title` quando não há), o campo (`field`, por
 * exemplo `customerManagerId`) e, no 409 do e-mail que já é usuário, a conta desse
 * e-mail (`existing.id`), para oferecer «Ligar a essa conta».
 */
export class InviteError extends ApiError {
  readonly field: string | null
  readonly existing: { id: string } | null

  constructor(
    status: number,
    message: string,
    extra: { field: string | null; existing: { id: string } | null },
  ) {
    super(status, message)
    this.name = 'InviteError'
    this.field = extra.field
    this.existing = extra.existing
  }
}

/**
 * POST /api/users/invite — cria o convite e (tenta) enviar o email.
 *
 * 409 = o email já pertence a um usuário (com `existing`) · 422 = domínio fora do
 * allowlist, cliente inativo, ou o gestor (de outro cliente, inativo, com conta, ou com
 * outro e-mail; `field: 'customerManagerId'`). Chegam como `InviteError`, com a frase do
 * servidor, que a tela mostra tal e qual — a regra de quem pode entrar é do servidor, e
 * reescrevê-la aqui seria manter duas versões da mesma política.
 */
export async function inviteUser(input: InviteUserInput): Promise<InviteResult> {
  const res = await fetch(`${USERS_URL}/invite`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) throw await inviteErrorOf(res)
  return (await res.json()) as InviteResult
}

async function inviteErrorOf(res: Response): Promise<InviteError> {
  let body: { title?: unknown; detail?: unknown; field?: unknown; existing?: unknown } = {}
  try {
    body = (await res.json()) as typeof body
  } catch {
    // Sem corpo legível: fica a frase por omissão.
  }
  const texto = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null)
  const existing = body.existing as { id?: unknown } | null | undefined
  return new InviteError(
    res.status,
    texto(body.detail) ?? texto(body.title) ?? 'Não foi possível enviar o convite',
    {
      field: texto(body.field),
      existing: existing && typeof existing.id === 'string' ? { id: existing.id } : null,
    },
  )
}

/** GET /api/users/invitations — os convites pendentes (nunca os já aceites). */
export async function listInvitations(signal?: AbortSignal): Promise<UserInvitation[]> {
  const res = await fetch(INVITATIONS_URL, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível carregar os convites'))
  }
  return (await res.json()) as UserInvitation[]
}

/** DELETE /api/users/invitations/:id — revoga o convite. 204 on success. */
export async function revokeInvitation(id: string): Promise<void> {
  const res = await fetch(`${INVITATIONS_URL}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemTitle(res, 'Não foi possível revogar o convite'))
  }
}

// ---------------------------------------------------------------------------
// Redefinição de senha (migration 0040) — a segunda vez que uma senha nasce, e
// a decisão é a mesma do convite: o admin NÃO define a senha de ninguém. Ele
// emite um link, e a pessoa escolhe a própria senha do outro lado.
//
// O lado PÚBLICO (a tela `/reset-password`, que corre sem sessão) vive em
// `lib/api/password-resets.ts` — perímetros diferentes, ficheiros diferentes,
// exatamente como o convite.
// ---------------------------------------------------------------------------

/**
 * Resposta do `POST /api/users/:id/reset-password`.
 *
 * `emailSent === false` NÃO é uma falha: o link existe, está gravado e é
 * válido — só o envio pelo Resend é que não saiu. Hoje esse é o caminho NORMAL
 * (o domínio não está verificado e todo o envio responde 403), e é por isso que
 * `resetUrl` vem sempre: quem redefine continua a poder entregar o link à mão.
 */
export interface PasswordResetResult {
  reset: {
    id: string
    userId: string
    /** Nome e email vêm na resposta para o toast dizer PARA QUEM o link é. */
    userName: string
    userEmail: string
    /** ISO — a partir daqui o link deixa de ser aceite (o servidor 404a). */
    expiresAt: string
    createdAt: string
  }
  resetUrl: string
  emailSent: boolean
  emailError: string | null
}

/**
 * POST /api/users/:id/reset-password — emite o link e (tenta) enviar o email.
 *
 * SEM CORPO, e é o ponto: não há nada que o chamador possa dizer sobre este ato.
 * Uma assinatura que aceitasse uma senha seria uma assinatura por onde um admin
 * poderia definir a senha de outra pessoa — o que este desenho existe para
 * impedir.
 *
 * 404 = o usuário não existe · 422 = a conta está desativada (uma senha nova não
 * lhe daria acesso nenhum, porque o `requireAuth` a recusa fail-closed). Ambos
 * chegam como `ApiError` com o `title` do servidor, que é a frase que a tela
 * mostra tal e qual — a regra é do servidor, e reescrevê-la aqui seria manter
 * duas versões da mesma política.
 */
export async function resetUserPassword(id: string): Promise<PasswordResetResult> {
  const res = await fetch(`${USERS_URL}/${id}/reset-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await problemTitle(res, 'Não foi possível gerar o link de redefinição'),
    )
  }
  return (await res.json()) as PasswordResetResult
}

// ---------------------------------------------------------------------------
// A frase de um problem+json, para as rotas do perfil de cliente e afins.
// ---------------------------------------------------------------------------

/**
 * Lê a frase de um problem+json preferindo o `detail` (pt-BR, que explica a
 * saída) ao `title`. As rotas do perfil de cliente e da ligação a um cliente põem o
 * motivo no `detail` — "este cliente está inativo" —, e o `title` sozinho não diz o
 * que fazer.
 */
async function problemDetail(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { title?: unknown; detail?: unknown }
    if (typeof body.detail === 'string' && body.detail.length > 0) return body.detail
    if (typeof body.title === 'string' && body.title.length > 0) return body.title
    return fallback
  } catch {
    return fallback
  }
}

// ============================================================
// O ACESSO DA GENESIS AO PORTAL (issue #584) — /api/users/:id/genesis-portal
// ============================================================

/** A chave da concessão de um usuário da Genesis. */
export function genesisPortalKey(userId: string) {
  return ['genesis-portal', userId] as const
}

/** GET /api/users/:id/genesis-portal. 404 · 422 se a conta não for da Genesis. */
export async function getGenesisPortal(
  userId: string,
  signal?: AbortSignal,
): Promise<GenesisPortalAccess> {
  const res = await fetch(`${USERS_URL}/${encodeURIComponent(userId)}/genesis-portal`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await problemDetail(res, 'Não foi possível carregar o acesso ao portal'),
    )
  }
  return (await res.json()) as GenesisPortalAccess
}

/**
 * PUT /api/users/:id/genesis-portal — substitui o perfil de cliente (só o cliente, desde
 * a Story 38.7), ou tira-o (`client: null`). Tirar ou trocar o perfil desliga a pessoa
 * dos gestores do cliente; o que ela vê no portal vem do gestor ligado a ela, no painel
 * do gestor. 422 = cliente inativo (a frase vem no `detail`).
 */
export async function updateGenesisPortal(
  userId: string,
  access: GenesisPortalAccess,
): Promise<GenesisPortalAccess> {
  const res = await fetch(`${USERS_URL}/${encodeURIComponent(userId)}/genesis-portal`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(access),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await problemDetail(res, 'Não foi possível salvar o acesso ao portal'),
    )
  }
  return (await res.json()) as GenesisPortalAccess
}

// ============================================================
// OS CLIENTES QUE A PESSOA GERE (Story 35.9) — /api/users/:id/managed-customers
// A própria pessoa muda a lista dela; um admin, a de qualquer pessoa da Genesis.
// ============================================================

/** A chave da carteira de clientes de uma pessoa da Genesis. */
export function managedCustomersKey(userId: string) {
  return ['managed-customers', userId] as const
}

/**
 * O 409 do `PUT`: há clientes na lista com outro gestor de conta, e a troca não veio
 * confirmada. `conflicts` diz quais, e quem os gere; nada foi gravado.
 */
export class ManagedCustomersConflictError extends ApiError {
  readonly conflicts: ManagedCustomerConflict[]
  constructor(message: string, conflicts: ManagedCustomerConflict[]) {
    super(409, message)
    this.name = 'ManagedCustomersConflictError'
    this.conflicts = conflicts
  }
}

/** GET /api/users/:id/managed-customers. 403 · 404 · 422 se a conta não for da Genesis. */
export async function getManagedCustomers(
  userId: string,
  signal?: AbortSignal,
): Promise<ManagedCustomers> {
  const res = await fetch(`${USERS_URL}/${encodeURIComponent(userId)}/managed-customers`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await problemDetail(res, 'Não foi possível carregar os clientes geridos'),
    )
  }
  return (await res.json()) as ManagedCustomers
}

/**
 * PUT /api/users/:id/managed-customers — substitui a lista inteira. 409 lança
 * `ManagedCustomersConflictError`, para a tela pedir a confirmação da troca e mandar o
 * mesmo pedido com `confirmTransfer: true`.
 */
export async function updateManagedCustomers(
  userId: string,
  input: ManagedCustomersUpdate,
): Promise<ManagedCustomers> {
  const res = await fetch(`${USERS_URL}/${encodeURIComponent(userId)}/managed-customers`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  })
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as {
      detail?: unknown
      conflicts?: ManagedCustomerConflict[]
    }
    throw new ManagedCustomersConflictError(
      typeof body.detail === 'string' ? body.detail : 'Há clientes com outro gestor de conta.',
      body.conflicts ?? [],
    )
  }
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await problemDetail(res, 'Não foi possível salvar os clientes geridos'),
    )
  }
  return (await res.json()) as ManagedCustomers
}

// ============================================================
// O VÍNCULO DA CONTA `customer` DA GENESIS (Story 35.5) — /api/users/:id/customer
// ============================================================

/**
 * O cliente com que a conta é ligada. Só o cliente, desde a Story 38.7: o que a conta vê
 * no portal vem do gestor do cliente ligado a ela, e até um admin a ligar a um ela não
 * entra no portal.
 */
export interface LinkCustomerInput {
  customerId: string
}

/**
 * PUT /api/users/:id/customer — liga a conta `customer` da Genesis (a que nasce sem
 * cliente pelo `gms-auth`) a um cliente. 422 = a conta não é essa, ou o cliente está
 * inativo (a frase vem no `detail`).
 */
export async function linkUserToCustomer(
  userId: string,
  input: LinkCustomerInput,
): Promise<LinkCustomerInput> {
  const res = await fetch(`${USERS_URL}/${encodeURIComponent(userId)}/customer`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    throw new ApiError(res.status, await problemDetail(res, 'Não foi possível ligar ao cliente'))
  }
  return (await res.json()) as LinkCustomerInput
}
